import { PERMISSION_CATEGORIES } from "../features/organisation/permissions";
import { ApiError } from "./api";

/** What to tell someone whose request was refused because of access, not a fault. */
export type AccessDenial = {
  kind: "permission" | "not_enabled" | "membership";
  title: string;
  detail: string;
};

const PERMISSION_LABELS = new Map<string, string>(
  PERMISSION_CATEGORIES.flatMap((category) =>
    category.permissions.map((p) => [p.id, `${p.label} (${category.label})`] as [string, string]),
  ),
);
const CATEGORY_LABELS = new Map<string, string>(PERMISSION_CATEGORIES.map((c) => [c.id, c.label]));

/**
 * Turn a set of failed requests into an access notice, or null when any of them failed
 * for another reason (that is a real error, and the caller should say so). Core phrases
 * a 403 one of three ways (Principal.denial_reason); each sends the reader somewhere else.
 */
export function describeAccessDenial(errors: unknown[]): AccessDenial | null {
  const failures = errors.filter(Boolean);
  if (!failures.length || !failures.every((e) => e instanceof ApiError && e.status === 403)) return null;
  const messages = failures.map((e) => (e as ApiError).message || "");

  if (messages.some((m) => /membership/i.test(m))) {
    return {
      kind: "membership",
      title: "You no longer have access to this organisation.",
      detail: "Ask its owner to add you again, or switch to another organisation.",
    };
  }

  const categories = messages
    .map((m) => /Feature category '([^']+)'/.exec(m)?.[1])
    .filter((c): c is string => Boolean(c));
  if (categories.length === messages.length) {
    const names = Array.from(new Set(categories.map((c) => CATEGORY_LABELS.get(c) ?? c)));
    return {
      kind: "not_enabled",
      title: `${names.join(", ")} ${names.length > 1 ? "aren't" : "isn't"} enabled for your organisation.`,
      detail: "Contact TechLadder support to have it turned on.",
    };
  }

  const permissions = messages
    .map((m) => /Permission denied: ([\w.]+)/.exec(m)?.[1])
    .filter((p): p is string => Boolean(p));
  const names = Array.from(new Set(permissions.map((p) => PERMISSION_LABELS.get(p) ?? p)));
  return {
    kind: "permission",
    title: "You don't have access to part of this page.",
    detail: names.length
      ? `Ask your organisation owner to grant: ${names.join(", ")}.`
      : "Ask your organisation owner to grant you access.",
  };
}
